<template>
  <div class="aroma-checkout">
    <aroma-secondary-nav title="确认订单" fallback="AromaProductDetail" />
    <div v-if="loading" class="aroma-page-state">
      <van-loading size="28px">加载中...</van-loading>
    </div>
    <van-empty v-else-if="errorMessage" :description="errorMessage">
      <van-button type="primary" size="small" @click="loadCheckout">重新加载</van-button>
    </van-empty>
    <template v-else-if="product && profile">
      <main class="aroma-checkout__content">
        <section class="aroma-form-section" aria-label="购买人信息">
          <van-field
            v-model.trim="orderProfile.phoneNumber"
            required
            type="tel"
            label="联系电话"
            placeholder="请输入联系电话"
            maxlength="11"
          />
          <van-field
            v-model.trim="orderProfile.realName"
            required
            label="姓名"
            placeholder="请输入姓名"
          />
          <van-field label="商品名称" value="纯植物精油复配芳香液" readonly class="is-disabled" />
          <van-field label="体验价" :value="`￥${price}`" readonly class="is-disabled" />
        </section>

        <div class="aroma-checkout__section-title">收货信息</div>
        <section class="aroma-form-section" aria-label="收货信息">
          <van-field
            v-model.trim="shipping.receiverName"
            required
            label="联系人"
            placeholder="请输入联系人"
          />
          <van-field
            v-model.trim="shipping.receiverMobile"
            required
            type="tel"
            label="联系电话"
            placeholder="请输入收货电话"
            maxlength="11"
          />
          <van-field
            :value="regionText"
            required
            readonly
            clickable
            label="联系地址"
            placeholder="请选择省市区"
            right-icon="arrow"
            @click="showAddressPicker = true"
          />
          <van-field
            v-model.trim="shipping.receiverAddress"
            required
            label="详细地址"
            placeholder="请输入详细地址"
          />
        </section>

        <aroma-pay-section
          :cashiers="cashiers"
          :selected-index="selectedCashierIndex"
          @select="selectedCashierIndex = $event"
        />
        <p class="aroma-checkout__record">备案号：浙ICP备2023008082号-1</p>
      </main>

      <footer class="aroma-checkout__footer">
        <div class="aroma-checkout__total">
          <span class="aroma-checkout__total-label">总计：</span>
          <strong class="aroma-checkout__total-price">￥{{ price }}</strong>
        </div>
        <van-button
          round
          type="primary"
          :loading="paying"
          :disabled="cashiers.length === 0"
          @click="submitOrder"
        >立即支付</van-button>
      </footer>

      <van-popup v-model="showAddressPicker" round position="bottom">
        <van-cascader
          v-model="cascaderValue"
          title="请选择省市区"
          :options="addressOptions"
          active-color="#596afd"
          @close="showAddressPicker = false"
          @finish="finishAddress"
        />
      </van-popup>

      <aroma-pay-back-dialog
        ref="payBackDialog"
        @confirm="openResult"
        @retry="retryPayment"
        @cancel="cancelPayment"
      />
    </template>
  </div>
</template>

<script>
import API from '@/api'
import AromaSecondaryNav from '@/components/aroma-report/AromaSecondaryNav.vue'
import AromaPayBackDialog from '@/components/aroma-report/AromaPayBackDialog.vue'
import AromaPaySection from '@/components/aroma-report/AromaPaySection.vue'
import { buildAddressOptions, readShipping, saveShipping, validateShipping } from '@/utils/aroma-address'
import {
  AROMA_PACKAGE_ID,
  AROMA_TASK_ID,
  buildAromaOrderPayload,
  buildPayResultUrl,
  filterCashiersForBrowser,
  formatPrice,
  getPayType
} from '@/utils/aroma-shop'
import {
  clearPendingOrder,
  isWeixinBrowser,
  savePendingOrder,
  startAlipay,
  startWechatPay
} from '@/utils/aroma-pay'
import { clearSniSession, getSniSession } from '@/utils/sni-session'

export default {
  name: 'AromaCheckout',

  components: {
    AromaSecondaryNav,
    AromaPayBackDialog,
    AromaPaySection
  },

  data () {
    return {
      loading: false,
      paying: false,
      errorMessage: '',
      task: null,
      product: null,
      profile: null,
      orderProfile: {
        phoneNumber: '',
        realName: ''
      },
      shipping: {
        receiverName: '',
        receiverMobile: '',
        region: [],
        receiverAddress: ''
      },
      cashiers: [],
      selectedCashierIndex: 0,
      addressOptions: [],
      showAddressPicker: false,
      cascaderValue: '',
      currentOrder: null
    }
  },

  computed: {
    sn () {
      return this.$route.query.sn || ''
    },

    session () {
      return getSniSession(this.sn)
    },

    wxCode () {
      return this.$route.query.code || ''
    },

    price () {
      return formatPrice(this.product?.packagePrice)
    },

    regionText () {
      return this.shipping.region.join('/')
    },

    selectedCashier () {
      return this.cashiers[this.selectedCashierIndex]
    }
  },

  mounted () {
    this.loadCheckout()
  },

  methods: {
    async loadCheckout () {
      this.loading = true
      this.errorMessage = ''
      try {
        const [packageResponse, taskResponse, cashierResponse, profile, addressResponse] = await Promise.all([
          API.aromaPackageDetail(AROMA_PACKAGE_ID),
          API.aromaOrderTask(AROMA_TASK_ID),
          API.aromaCashiers(),
          API.sniCurrent(this.session.token),
          fetch('https://bajie-sleep.oss-cn-hangzhou.aliyuncs.com/pca.json').then(response => response.json())
        ])
        if (packageResponse?.code !== '1' || !packageResponse.data) throw new Error(packageResponse?.msg || '商品暂不可用')
        if (taskResponse?.code !== '1' || !taskResponse.data) throw new Error(taskResponse?.msg || '商品暂不可用')
        if (cashierResponse?.code !== '1') throw new Error(cashierResponse?.msg || '支付方式加载失败')

        this.product = packageResponse.data
        this.task = taskResponse.data
        this.profile = profile
        this.orderProfile = {
          phoneNumber: profile.phoneNumber || '',
          realName: profile.realName || ''
        }
        const saved = readShipping(this.sn)
        this.shipping = {
          receiverName: saved.receiverName || profile.realName || '',
          receiverMobile: saved.receiverMobile || profile.phoneNumber || '',
          region: Array.isArray(saved.region) ? saved.region : [],
          receiverAddress: saved.receiverAddress || ''
        }
        this.cashiers = filterCashiersForBrowser(
          cashierResponse.data || [],
          isWeixinBrowser(window.navigator.userAgent)
        )
        this.addressOptions = buildAddressOptions(addressResponse)
      } catch (error) {
        if (error?.code === '8' || error?.code === '11') {
          clearSniSession(this.sn)
          this.$router.replace({
            name: 'AromaReportLogin',
            query: { sn: this.sn, redirect: this.$route.fullPath }
          })
          return
        }
        this.errorMessage = error?.message || '订单信息加载失败'
      } finally {
        this.loading = false
      }
    },

    finishAddress ({ selectedOptions }) {
      this.shipping.region = selectedOptions.map(item => item.value)
      this.showAddressPicker = false
    },

    validateForm () {
      if (!this.orderProfile.phoneNumber) return '请填写联系电话'
      if (!/^1\d{10}$/.test(this.orderProfile.phoneNumber)) return '联系电话不正确'
      if (!this.orderProfile.realName) return '请填写姓名'
      if (!this.profile?.birthday) return '个人资料缺少生日，请重新登录后完善资料'
      return validateShipping(this.shipping)
    },

    async submitOrder () {
      const message = this.validateForm()
      if (message) {
        this.$toast(message)
        return
      }
      if (!this.selectedCashier) {
        this.$toast('当前环境暂无可用支付方式，请稍后重试')
        return
      }

      this.paying = true
      try {
        const [packageResponse, taskResponse] = await Promise.all([
          API.aromaPackageDetail(AROMA_PACKAGE_ID),
          API.aromaOrderTask(AROMA_TASK_ID)
        ])
        if (packageResponse?.code !== '1' || !packageResponse.data || taskResponse?.code !== '1' || !taskResponse.data) {
          throw new Error('商品价格校验失败，请稍后重试')
        }
        if (Number(packageResponse.data.packagePrice) !== Number(this.product.packagePrice)) {
          this.product = packageResponse.data
          this.task = taskResponse.data
          this.$toast('商品价格已更新，请确认后支付')
          return
        }

        this.product = packageResponse.data
        this.task = taskResponse.data
        saveShipping(this.sn, this.shipping)
        clearPendingOrder(this.sn)

        const profile = {
          ...this.profile,
          phoneNumber: this.orderProfile.phoneNumber,
          realName: this.orderProfile.realName
        }
        const payload = buildAromaOrderPayload({
          task: this.task,
          packageData: this.product,
          profile,
          shipping: this.shipping,
          cashierTypeId: this.selectedCashier.id,
          jsCode: this.wxCode,
          returnUrl: buildPayResultUrl(window.location.origin, this.sn)
        })
        const response = await API.aromaCreateOrder(payload)
        if (response?.code !== '1' || !response.data) throw new Error(response?.msg || '创建订单失败')

        const payType = getPayType(this.selectedCashier)
        this.currentOrder = {
          orderId: response.data.oid,
          payType,
          payData: response.data
        }
        savePendingOrder(this.sn, this.currentOrder)
        await this.startPayment()
      } catch (error) {
        if (this.currentOrder) this.$refs.payBackDialog.show(this.currentOrder.payType)
        else this.$toast(error?.message || error?.err_msg || '支付失败')
      } finally {
        this.paying = false
      }
    },

    async startPayment () {
      if (this.currentOrder.payType === 'wechat') {
        await startWechatPay(this.currentOrder.payData)
        this.openResult()
        return
      }
      startAlipay(this.currentOrder.payData)
    },

    retryPayment () {
      this.startPayment().catch(() => this.$refs.payBackDialog.show(this.currentOrder.payType))
    },

    cancelPayment () {
      this.currentOrder = null
    },

    openResult () {
      this.$router.replace({
        name: 'AromaPayResult',
        query: { sn: this.sn, orderId: this.currentOrder?.orderId }
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/css/aroma-commerce.scss';

.aroma-checkout {
  min-height: 100%;
  padding-bottom: calc(80px + env(safe-area-inset-bottom));
  background: #f4f4f4;
  box-sizing: border-box;

  &__content {
    padding-top: 8px;
  }

  &__section-title {
    height: 42px;
    padding: 0 16px;
    color: #8a8f98;
    font-size: 14px;
    line-height: 42px;
  }

  &__record {
    padding: 16px 12px;
    color: #646a73;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
  }

  &__footer {
    position: fixed;
    z-index: 10;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    min-height: 72px;
    align-items: center;
    padding: 0 15px env(safe-area-inset-bottom);
    background: #f4f4f4;
    box-sizing: border-box;

    .van-button {
      width: 216px;
      max-width: 58%;
      min-width: 132px;
      height: 40px;
      flex: 0 0 216px;
      padding: 0 20px;
      font-size: 16px;
      background: #596afd;
      border-color: #596afd;
    }
  }

  &__total {
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: baseline;
  }

  &__total-label {
    color: #8a8f98;
    font-size: 14px;
  }

  &__total-price {
    color: #ff4c45;
    font-size: 20px;
    font-weight: 600;
  }
}

.aroma-form-section {
  background: #fff;

  ::v-deep .van-cell {
    min-height: 52px;
    align-items: center;
    padding: 10px 16px;
    font-size: 16px;
  }

  ::v-deep .van-field__label {
    width: 112px;
    color: #1f2329;
  }

  .is-disabled {
    background: #f4f4f4;
  }
}

@media (min-width: 768px) {
  .aroma-checkout {
    max-width: 520px;
    margin: 0 auto;

    &__footer {
      right: 50%;
      left: 50%;
      width: 520px;
      transform: translateX(-50%);
      box-sizing: border-box;
    }
  }
}
</style>
